import { useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import useIsDesktop from "@/hooks/useIsDesktop";
import appendLoopingSlickDots from "@/Common/LoopingSlickDots";
import styles from "./ThingsstodoNeedToKnow.module.css";

const FALLBACK_TABS = [
  { id: "weather", title: "Weather" },
  { id: "bestTime", title: "Best time to visit" },
  { id: "festivals", title: "Top festivals" },
  { id: "tips", title: "Travel tips & hacks" },
  { id: "landmarks", title: "Top Landmarks" },
];

const FALLBACK_CONTENT = {
  weather: { type: "weather", data: [] },
  bestTime: { type: "text", text: "" },
  festivals: { type: "festival", data: [] },
  tips: { type: "tips", data: [] },
  landmarks: { type: "landmarks", data: [] },
};

function renderIcon(item) {
  if (item.iconUrl) {
    return (
      <img
        src={item.iconUrl}
        alt={item.iconAlt || item.title || ""}
        className={styles.thingstodoNeedToKnowTipIconImage}
      />
    );
  }

  return item.icon || null;
}

export default function ThingsstodoNeedToKnow({ needToKnow }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const { isDesktop: isAbove900, mounted: mounted900 } = useIsDesktop(900);
  const { isDesktop: isAbove1200, mounted: mounted1200 } = useIsDesktop(1200);
  const { isDesktop: isAbove600, mounted: mounted600 } = useIsDesktop(600);
  const sliderRef = useRef(null);

  const viewportReady = mounted900 && mounted1200 && mounted600;
  const isWideDesktop = viewportReady && isAbove1200;

  /** >1200: web grid/slick · 901–1200: 4 · 601–900: 2 · ≤600: 1 */
  const responsiveSlidesToShow = useMemo(() => {
    if (!viewportReady || isAbove1200) return null;
    if (isAbove900) return 4;
    if (isAbove600) return 2;
    return 1;
  }, [viewportReady, isAbove1200, isAbove900, isAbove600]);

  const tabs = needToKnow?.tabs?.length ? needToKnow.tabs : FALLBACK_TABS;
  const tabContent = needToKnow?.content || FALLBACK_CONTENT;

  const activeTabId = tabs[selectedTab]?.id || tabs[0]?.id || "weather";
  const currentContent = useMemo(
    () => tabContent[activeTabId] || {},
    [tabContent, activeTabId]
  );

  const hasContent = useMemo(() => {
    if (!needToKnow?.tabs?.length) return false;

    return Object.values(tabContent).some((entry) => {
      if (entry.type === "text") return Boolean(entry.text);
      return Array.isArray(entry.data) && entry.data.length > 0;
    });
  }, [needToKnow, tabContent]);

  const compactSlickSettings = useMemo(
    () => ({
      dots: true,
      infinite: false,
      speed: 400,
      slidesToShow: responsiveSlidesToShow ?? 1,
      slidesToScroll: 1,
      arrows: false,
      swipe: true,
      draggable: true,
      touchMove: true,
      touchThreshold: 8,
      adaptiveHeight: true,
      dotsClass: `slick-dots ${styles.carouselDots}`,
      appendDots: appendLoopingSlickDots(`slick-dots ${styles.carouselDots}`),
    }),
    [responsiveSlidesToShow]
  );

  if (!hasContent) return null;

  const getSlidesToShow = () => {
    if (currentContent.type === "landmarks") return 5;
    if (currentContent.type === "tips") return 4;
    return 3;
  };

  const getGridClass = () => {
    if (currentContent.type === "landmarks") return styles.thingstodoNeedToKnowGrid5;
    if (currentContent.type === "tips") return styles.thingstodoNeedToKnowGrid4;
    return styles.thingstodoNeedToKnowGrid3;
  };

  const desktopSlickSettings = {
    dots: false,
    infinite: false,
    speed: 400,
    slidesToShow: getSlidesToShow(),
    slidesToScroll: 1,
    arrows: false,
    swipe: false,
    draggable: false,
  };

  const renderContent = () => {
    if (currentContent.type === "text") {
      return (
        <p className={styles.thingstodoNeedToKnowTextBlock}>
          {currentContent.text}
        </p>
      );
    }

    const items = currentContent.data || [];

    const renderCardItem = (item) => {
      switch (currentContent.type) {
        case "weather":
          return (
            <article className={styles.thingstodoNeedToKnowWeatherCard}>
              <div className={styles.thingstodoNeedToKnowCardHeader}>
                <span className={styles.thingstodoNeedToKnowWeatherIcon}>
                  {renderIcon(item)}
                </span>
                <h3 className={styles.thingstodoNeedToKnowCardTitle}>
                  {item.title}
                </h3>
              </div>
              {item.subtitle ? (
                <p className={styles.thingstodoNeedToKnowSubtitle}>
                  {item.subtitle}
                </p>
              ) : null}
              {item.description ? (
                <p className={styles.thingstodoNeedToKnowDesc}>
                  {item.description}
                </p>
              ) : null}
            </article>
          );

        case "festival":
          return (
            <article className={styles.thingstodoNeedToKnowFestivalCard}>
              <div className={styles.thingstodoNeedToKnowFestivalImgWrap}>
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.title || ""}
                    className={styles.thingstodoNeedToKnowImg}
                  />
                ) : null}
              </div>
              <div className={styles.thingstodoNeedToKnowFestivalContent}>
                <h3 className={styles.thingstodoNeedToKnowFestivalTitle}>
                  {item.title}
                </h3>
                <p className={styles.thingstodoNeedToKnowFestivalDesc}>
                  {item.description}
                </p>
              </div>
            </article>
          );

        case "tips":
          return (
            <article className={styles.thingstodoNeedToKnowTipsCard}>
              <div className={styles.thingstodoNeedToKnowTipIconCircle}>
                {renderIcon(item)}
              </div>
              <div className={styles.thingstodoNeedToKnowTipContent}>
                <h3 className={styles.thingstodoNeedToKnowTipTitle}>
                  {item.title}
                </h3>
                <p className={styles.thingstodoNeedToKnowTipSubtitle}>
                  {item.subtitle}
                </p>
              </div>
            </article>
          );

        case "landmarks":
          return (
            <article className={styles.thingstodoNeedToKnowLandmarkCard}>
              <div className={styles.thingstodoNeedToKnowLandmarkImgWrap}>
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.title || ""}
                    className={styles.thingstodoNeedToKnowImg}
                  />
                ) : null}
              </div>
              <h3 className={styles.thingstodoNeedToKnowLandmarkTitle}>
                {item.title}
              </h3>
              <p className={styles.thingstodoNeedToKnowLandmarkSub}>
                {item.subtitle}
              </p>
            </article>
          );

        default:
          return null;
      }
    };

    if (!items.length) return null;

    const useDesktopGrid =
      isWideDesktop && items.length <= getSlidesToShow();

    const compactSlides = Math.min(
      responsiveSlidesToShow ?? 1,
      items.length
    );
    const useCompactMobileShell = viewportReady && !isAbove900;
    const compactSettings = {
      ...compactSlickSettings,
      slidesToShow: compactSlides,
      dots: compactSlides < items.length,
    };

    return useDesktopGrid ? (
      <div
        className={`${styles.thingstodoNeedToKnowGrid} ${getGridClass()}`}
      >
        {items.map((item) => (
          <div key={item.id} className={styles.thingstodoNeedToKnowGridItem}>
            {renderCardItem(item)}
          </div>
        ))}
      </div>
    ) : isWideDesktop ? (
      <div
        className={`${styles.thingstodoNeedToKnowSlickWrap} ${
          items.length <= getSlidesToShow()
            ? styles.thingstodoNeedToKnowAlignLeft
            : ""
        }`}
      >
        <Slider ref={sliderRef} {...desktopSlickSettings}>
          {items.map((item) => (
            <div key={item.id} className={styles.thingstodoNeedToKnowSlide}>
              {renderCardItem(item)}
            </div>
          ))}
        </Slider>
      </div>
    ) : (
      <div
        className={
          useCompactMobileShell
            ? styles.thingstodoNeedToKnowSlickWrapMobile
            : styles.thingstodoNeedToKnowSlickWrap
        }
        key={`${activeTabId}-compact-${compactSlides}`}
      >
        <Slider {...compactSettings}>
          {items.map((item) => (
            <div key={item.id} className={styles.thingstodoNeedToKnowSlide}>
              {renderCardItem(item)}
            </div>
          ))}
        </Slider>
      </div>
    );
  };

  return (
    <section
      className={`${styles.thingstodoNeedToKnowSection} standard-padding`}
    >
      <div className={styles.thingstodoNeedToKnowHeader}>
        <h2 className="standard-heading">What You Need To Know</h2>
        <p className="standard-subheading">
          Uncover your ideal escape, customized to your preferences.
        </p>
      </div>

      <div className={styles.thingstodoNeedToKnowTabsShell}>
        <div
          className={styles.thingstodoNeedToKnowTabs}
          role="tablist"
          aria-label="What You Need To Know Categories"
        >
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selectedTab === index}
              className={`${styles.thingstodoNeedToKnowTab} ${
                selectedTab === index
                  ? styles.thingstodoNeedToKnowTabActive
                  : ""
              }`}
              onClick={() => setSelectedTab(index)}
            >
              {tab.title}
            </button>
          ))}
        </div>
      </div>

      {renderContent()}
    </section>
  );
}
